import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { buttonVariants } from './button.variants.ts'

/**
 * Button triggers an action. For navigation, use a Link instead.
 *
 * **States** — style against these attributes:
 * - `:disabled` / the `disabled` prop — non-interactive, dimmed.
 * - `:focus-visible` — keyboard focus ring.
 * - `[aria-invalid]` — invalid form-control styling.
 *
 * @summary Action trigger with primary/secondary/tertiary/text variants × 6 color families (intent).
 */
function Button(props: Button.Props) {
  const { className, variant = 'primary', size = 'medium', intent = 'base', ...rest } = props
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, intent, className }))}
      {...rest}
    />
  )
}

namespace Button {
  type Variants = NonNullable<VariantProps<typeof buttonVariants>>
  export type Props = ButtonPrimitive.Props & {
    /** Visual emphasis of the button. @default 'primary' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Height & horizontal padding, or a square icon size. @default 'medium' */
    size?: NonNullable<Variants['size']> | undefined
    /** Color family of the button. @default 'base' */
    intent?: NonNullable<Variants['intent']> | undefined
  }
}

export { Button, buttonVariants }
